import React, { useEffect, useRef } from 'react';
import { gameAudio } from '@root/lib';
import { phaserBridge } from '@root/phaser/bridge';
import { createGame } from '@root/phaser/createGame';
import { useActions } from '@root/store/store';
import BettingPill from '../BettingPill';
import PhaserOverlay from '../PhaserOverlay';
import styles from './GameFrame.module.scss';

const GameFrame: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { setSceneReady, setLoadProgress, setLoadError } = useActions();

  useEffect(() => {
    if (!containerRef.current) return;
    // Subscribe before game starts or loader stays on screen.
    const off = phaserBridge.onPhaserEvent((evt) => {
      switch (evt.type) {
        case 'SCENE_READY':
          setSceneReady(evt.scene);
          if (evt.scene === 'Level') gameAudio.startMusic();
          break;
        case 'LOAD_PROGRESS':
          setLoadProgress(evt.value);
          break;
        case 'SCENE_ERROR':
          setLoadError(evt.error);
          break;
      }
    });
    const el = containerRef.current;
    const game = createGame({ parent: el });

    // Device pixels, or the board is soft on retina. Capped at 2.
    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { clientWidth: w, clientHeight: h } = el;
      if (!w || !h) return;
      game.scale.resize(w * dpr, h * dpr);
      game.canvas.style.width = `${w}px`;
      game.canvas.style.height = `${h}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);

    return () => {
      off();
      observer.disconnect();
      game.destroy(true);
      gameAudio.stopMusic();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <div ref={containerRef} className={styles.content} />
      <PhaserOverlay />
      <BettingPill />
    </div>
  );
};

export default GameFrame;
